<template>
  <div
    v-if="shouldShow"
    :class="['invalidation-info', `invalidation-info--${variant}`, statusClass]">
    <a-spin
      v-if="isInProgress"
      size="small" />
    <span>{{ text }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import dayjs from 'dayjs';

const props = defineProps({
  invalidations: {
    type: Array,
    default: () => [],
  },
  showInProgress: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'breadcrumb', 'button'].includes(value),
  },
});

const getInvalidationTimestamp = (item) => {
  const value = item.finished_at || item.created_at || item.updated_at;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortedInvalidations = computed(() => [...props.invalidations]
  .sort((a, b) => getInvalidationTimestamp(b) - getInvalidationTimestamp(a)));

const lastInvalidation = computed(() => sortedInvalidations.value[0]);
const lastInvalidationStatus = computed(() => (lastInvalidation.value?.status || '').toLowerCase());
const isFailed = computed(() => lastInvalidationStatus.value === 'failed');
const isInProgress = computed(() => Boolean(lastInvalidation.value)
  && lastInvalidation.value.finished_at === null
  && !isFailed.value);
const isSuccess = computed(() => Boolean(lastInvalidation.value?.finished_at) && !isFailed.value);
const statusClass = computed(() => ({
  'invalidation-info--failed': isFailed.value,
  'invalidation-info--success': isSuccess.value,
}));

const shouldShow = computed(() => {
  if (!props.invalidations.length) {
    return true;
  }

  if (isFailed.value) {
    return true;
  }

  if (isInProgress.value) {
    return props.showInProgress;
  }

  return Boolean(lastInvalidation.value?.finished_at);
});

const text = computed(() => {
  if (!props.invalidations.length) {
    return 'Инвалидаций кэша не было';
  }

  if (isFailed.value) {
    return 'Инвалидация завершилась с ошибкой';
  }

  if (isInProgress.value) {
    return 'Инвалидация в процессе';
  }

  return `Последняя инвалидация: ${dayjs(lastInvalidation.value.finished_at).format('DD.MM.YYYY HH:mm:ss')}`;
});
</script>

<style scoped>
.invalidation-info {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: .5rem;
  color: rgba(0, 0, 0, .65);
  font-size: 14px;
  line-height: 1.5715;
  white-space: nowrap;
}

.invalidation-info--breadcrumb {
  color: rgba(0, 0, 0, .45);
}

.invalidation-info--button {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: .5rem;
  margin-bottom: .5rem;
  color: rgba(0, 0, 0, .85);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5715;
  text-align: center;
}

.invalidation-info--failed {
  color: #f5222d;
}

.invalidation-info--success {
  color: #52c41a;
}
</style>
